import React from 'react';
import { Link } from 'react-router-dom';
import MilanLogo from './icons/MilanLogo';

const legalContent = {
    tnc: {
        title: "Terms & Conditions",
        lastUpdated: "14 June 2026",
        sections: [
            {
                heading: "1. Acceptance of Terms",
                content: "By accessing or using Milan, you agree to these Terms. If you do not agree, do not use the app."
            },
            {
                heading: "2. Eligibility",
                content: "You must be at least 18 years old. You confirm that all information you provide is accurate and truthful."
            },
            {
                heading: "3. Account & Access",
                content: "One person, one account. You are responsible for activity on your account. We can suspend or terminate accounts that violate these terms or harm the platform."
            },
            {
                heading: "4. Purpose of the App",
                content: "Milan is a high-intent relationship platform. It enforces one active romantic relationship at a time. You agree not to misuse the system to bypass this design."
            },
            {
                heading: "5. User Content",
                content: "You retain ownership of content you upload. You grant Milan a license to host, display, and process your content for app functionality. You must not upload illegal, abusive, sexual, or misleading content."
            },
            {
                heading: "6. AI-Generated Insights",
                content: "Milan uses AI to generate reflections, insights, and match explanations. These are assistive, not factual claims or guarantees. You understand AI outputs may be imperfect."
            },
            {
                heading: "7. Acceptable Use",
                content: "You agree not to: Harass, threaten, or exploit others; Scrape, reverse-engineer, or abuse the system; Impersonate others; Use Milan for commercial solicitation."
            },
            {
                heading: "8. Matches, Unmatching & Slate Wipe",
                content: "Matches are exclusive. When you match, other pending likes are cancelled automatically. Unmatching resets the match state. No guarantees of rediscovery."
            },
            {
                heading: "9. Termination",
                content: "You may delete your account at any time. We may suspend or terminate accounts for violations or safety reasons."
            },
            {
                heading: "10. Disclaimer",
                content: "Milan is provided “as is”. We do not guarantee matches, relationships, or outcomes."
            },
            {
                heading: "11. Limitation of Liability",
                content: "To the maximum extent allowed by law, Milan is not liable for indirect, incidental, or emotional damages."
            },
            {
                heading: "12. Changes",
                content: "We may update these Terms. Continued use means acceptance of updates."
            },
            {
                heading: "13. Contact",
                content: "For questions or support, please submit a request via our Support Page."
            }
        ]
    },
    privacy: {
        title: "Privacy Policy",
        lastUpdated: "14 June 2026",
        sections: [
            {
                heading: "1. What We Collect",
                content: "Information you provide: Name, email, Date of birth, gender, preferences, Photos and profile details, Reflection answers and messages. Automatically collected: Device and usage data, App interactions and timestamps, Location (approximate, for matching)."
            },
            {
                heading: "2. How We Use Data",
                content: "We use your data to: Create and manage your account, Generate matches and selections, Power AI reflections and insights, Improve product performance and safety."
            },
            {
                heading: "3. AI & Data Processing",
                content: "Reflection answers and interactions may be processed by AI models. Raw internal scores or vectors are never shown to other users. AI is used to assist matching, not to judge or rank your worth."
            },
            {
                heading: "4. Data Sharing",
                content: "We do not sell your data. We may share limited data with: Cloud and infrastructure providers, AI service providers (only what is required), Legal authorities if required by law."
            },
            {
                heading: "5. Messages & Privacy",
                content: "Messages are private between matched users. We do not read messages unless required for safety, abuse, or legal reasons."
            },
            {
                heading: "6. Data Retention",
                content: "Active data is stored while your account exists. Deleted accounts trigger permanent deletion of profile, matches, and messages within a reasonable time."
            },
            {
                heading: "7. Your Rights",
                content: "You can: Access your data, Edit your profile, Delete your account and data permanently, Control notification and privacy settings."
            },
            {
                heading: "8. Security",
                content: "We use industry-standard security practices, but no system is 100% secure."
            },
            {
                heading: "9. Children",
                content: "Milan is strictly 18+. We do not knowingly collect data from minors."
            },
            {
                heading: "10. Changes",
                content: "We may update this policy. Continued use means acceptance."
            },
            {
                heading: "11. Contact",
                content: "For privacy questions, please submit a request via our Support Page."
            }
        ]
    }
};

const LegalPage = ({ type }) => {
    const content = legalContent[type];

    if (!content) return null;

    return (
        <>
            <div className="brand-top-fade brand-top-fade--visible" aria-hidden="true" />
            <div className="brand-mark brand-mark--compact" role="img" aria-label="Milan">
                <MilanLogo className="brand-mark__logo" aria-hidden="true" />
            </div>
            <div className="bg-background min-h-screen">
                <div className="max-w-[800px] mx-auto px-5 sm:px-6 md:px-8 pt-28 pb-20 sm:pt-40 sm:pb-32">
                    <div className="reveal">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.020em] leading-[1.1] mb-4">
                        {content.title}
                    </h1>
                    <p className="text-sm text-muted-foreground mb-20">
                        Last updated: {content.lastUpdated}
                    </p>

                    <div className="space-y-16">
                        {content.sections.map((section, index) => (
                            <div key={index} className="reveal">
                                <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-foreground">
                                    {section.heading}
                                </h2>
                                <p className="text-lg leading-relaxed text-muted-foreground">
                                    {section.content}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </>
    );
};

export default LegalPage;
